import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";

import TableComponent from "components/common/TableComponent";
import CustomDropdown from "components/common/Dropdown";
import PageLoader from "components/common/PageLoader";
import { AlertType } from "components/common/Snackbar";

import { getEmployeePaySlips } from "services/BambooHrTabs/salarydetails";

import { showSnackbar } from "store/actions/Utility";

import { cleanCurrencyString } from "util/utils";
import { baseUrl } from "util/APIBaseUrl";
import getRequest from "util/APIHelperGet";
import { isNewPayslipId } from "util/payslipDataMapper";
import { formatToMonthYear } from "helpers";

import NoPaySlipsFound from "./NoPaySlips";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Chip } from "@mui/material";

function PaySlips({ employeeID }) {
  const currentYear = new Date().getFullYear().toString();
  const [year, setYear] = useState(currentYear);
  const [paySlips, setPaySlips] = useState([]);
  const [yearsAvailable, setYearsAvailable] = useState([currentYear]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  const dispatch = useDispatch();

  const fetchPaySlips = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getEmployeePaySlips(employeeID, year, page, rowsPerPage);

      const formattedSlips = data?.payload?.rows.map((slip) => ({
        ...slip,
        payrollMonth: formatToMonthYear(slip?.payslipMonth),
      }));
      setPaySlips(formattedSlips);
      setTotalCount(data.totalCount);
      const availableYears = data?.payload?.years;
      if (availableYears && availableYears.length > 0) {
        setYearsAvailable(availableYears);

        const isSelectedYearAvailable = availableYears.includes(year);
        if (!isSelectedYearAvailable && availableYears[0]) {
          setYear(availableYears[0]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch pay slips:", error);
      dispatch(showSnackbar({ dispatch, type: "error", message: "Failed to fetch pay slips" }));
    } finally {
      setIsLoading(false);
    }
  }, [employeeID, year, page, rowsPerPage, dispatch]);

  useEffect(() => {
    fetchPaySlips();
  }, [fetchPaySlips]);

  const handleYearChange = (selectedYear) => {
    setYear(selectedYear);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const downloadPayslip = async (payrollId) => {
    const shouldUseNewApi = isNewPayslipId(payrollId);

    const downloadUrl = shouldUseNewApi
      ? `${baseUrl}/employees/${employeeID}/payslips/${payrollId}?action=pdf`
      : `${baseUrl}/employee/${employeeID}/payroll/${payrollId}?action=pdf`;

    setIsDownloading(true);
    try {
      const response = await getRequest(downloadUrl, true, "export");

      if (!response?.ok) {
        dispatch(
          showSnackbar({
            type: AlertType.error,
            message: "Payslip is not available, Please contact support ",
            dispatch,
          })
        );
        return;
      }
      const url = window.URL.createObjectURL(response?.payload);
      const link = document.createElement("a");
      link.style.display = "none";
      link.href = url;

      link.download = "Salary Slip";
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      dispatch(showSnackbar({ dispatch, type: "success", message: "Payslip downloaded successfully!" }));
    } finally {
      setIsDownloading(false);
    }
  };

  const handleAction = useCallback((action, row) => {
    const { id: payrollId } = row;
    if (action.label === "Download") {
      downloadPayslip(payrollId);
    }
  }, []);

  const CTS_EMPTY = "—";

  const renderPayslipTypeChip = (value, row) => {
    const isCTS = row?.payslipType === "CTS";
    const isGratification = row?.payslipType === "Gratification";

    let label = "Monthly";
    if (isCTS) label = "CTS";
    else if (isGratification) label = "Gratification";

    let chipBackground = "#E4F1FB";
    if (isCTS) chipBackground = "#E8E2F5";
    else if (isGratification) chipBackground = "#E6F4EA";

    let chipColor = "#1161D5";
    if (isCTS) chipColor = "#4A0F70";
    else if (isGratification) chipColor = "#1E7A34";
    return (
      <Chip
        size="small"
        variant="outlined"
        label={label}
        sx={{
          "&.MuiChip-root": { height: "24px !important", border: "none", padding: "4px, 8px, 4px, 8px", gap: "10px" },
          "& .MuiChip-label": {
            fontSize: "12px",
            lineHeight: "16px",
            fontFamily: "Poppins",
            fontWeight: 500,
            letterSpacing: "0.5px",
          },
          background: chipBackground,
          color: chipColor,
        }}
      />
    );
  };

  const renderAllowanceOrDeduction = (value, row) => {
    if (row?.payslipType === "CTS") {
      return <p className="para-body-m-regular">{CTS_EMPTY}</p>;
    }
    return <p className="para-body-m-regular">{cleanCurrencyString(value)}</p>;
  };

  const renderNetPay = (value, row) => {
    if (row?.payslipType === "CTS") {
      const formatted = row?.cts ? cleanCurrencyString(row.cts) : "";
      return <p className="para-body-m-regular">{formatted || CTS_EMPTY}</p>;
    }
    return <p className="para-body-m-regular">{cleanCurrencyString(value)}</p>;
  };

  const columns = [
    { id: "payrollMonth", label: "Month" },
    {
      id: "payslipType",
      label: "Type",
      render: renderPayslipTypeChip,
    },
    {
      id: "totalAdditions",
      label: "Allowance",
      render: renderAllowanceOrDeduction,
    },
    {
      id: "totalDeductions",
      label: "Deductions",
      render: renderAllowanceOrDeduction,
    },
    {
      id: "netSalary",
      label: "Net Pay",
      render: renderNetPay,
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      type: "action",
      actions: [
        {
          icon: <FileDownloadOutlinedIcon className="payslip-download-icon" />,
          label: "Download",
        },
      ],
    },
  ];

  const yearOptions = (yearsAvailable?.length > 0 ? yearsAvailable : [currentYear]).map((filterYear) => ({
    key: filterYear,
    label: filterYear,
  }));

  return (
    <div className="flex flex-col">
      {(isLoading || isDownloading) && <PageLoader />}
      <div className="pt-175 px-2">
        <h4 className="m-0 heading-h4-semibold">Pay Slips</h4>
        <div className="payslip-filter para-body-s-medium flex gap-075 items-center justify-start py-075">
          <span>Filter:</span>
          <CustomDropdown
            options={yearOptions}
            onSelect={handleYearChange}
            buttonLabel={year}
            disabled={yearsAvailable.length < 2}
            style={{
              fontFamily: "var(--font-family-paragraph, Inter)",
              height: "28px",
              padding: "2px 12px",
              borderRadius: "var(--border-radius-full)",
              fontSize: "var(--font-size-sm, 12px)",
              fontWeight: "var(--font-weight-medium)",
              lineHeight: "var(--line-height-sm, 18px)",
              border: "1px solid var(--color-border-neutral, #888)",
              opacity: yearsAvailable.length < 2 ? 0.5 : 1,
              cursor: yearsAvailable.length < 2 ? "not-allowed" : "pointer",
            }}
          />
        </div>
      </div>
      {paySlips.length > 0 ? (
        <TableComponent
          columns={columns}
          data={paySlips}
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onAction={handleAction}
        />
      ) : (
        <NoPaySlipsFound />
      )}
    </div>
  );
}

export default PaySlips;
